import PostIcon from "@/assets/colorable/post.svg?react";
import ReelIcon from "@/assets/colorable/reel.svg?react";
import StoryIcon from "@/assets/colorable/story.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import UgcIcon from "@/assets/colorable/ugc.svg?react";

import type {
  CampaignFeedSerializer,
  CampaignManagementInfluencerSerializer,
  PromotionCampaignAssetSerializer,
} from "@/generated/DjangoTypes";
import type {TContentStats, TSubmissionStatus} from "@/hooks/useContentApproval";
import {BONUS_POOL_MIN_CASH_REWARD, type TCampaignSize} from "@/store/campaignForm";
import type {TCarouselMedia} from "@/store/carousel";
import {CONTENT_APPROVAL_MAX_REVISIONS} from "@/store/constants";
import type {
  TCampaignApplicationAccepted,
  TCampaignApplicationReporting,
  TCampaignType,
  TContent,
  TContentSubmission,
  TInstagramContent,
  TInstagramMedia,
} from "@/store/types";
import dayjs from "dayjs";
import {countBy, snakeCase, type Dictionary} from "lodash";

const CAMPAIGN_BUFFER_DAYS = 2;
const CAMPAIGN_BUFFER_DAYS_CONTENT_APPROVAL = 7;

export const CAPTION_LIMIT = 2200;
export const TIKTOK_CAPTION_LIMIT = 4000;

export const CAMPAIGN_SIZE_RANGES: Record<TCampaignSize, {max: number; min: number}> = {
  "5-10": {max: 10, min: 5},
  "10-15": {max: 15, min: 10},
  "20-50": {max: 50, min: 20},
  "50-100": {max: 100, min: 50},
  "100+": {max: 100, min: 100},
};

export const getCampaignSizeOptions = ({hasSubscription, planName}: {hasSubscription: boolean; planName?: string}) =>
  (Object.keys(CAMPAIGN_SIZE_RANGES) as TCampaignSize[]).filter(
    size => size !== "5-10" || !hasSubscription || planName === "ADVANCED",
  );

type TCompletedWithBuffer = {completed?: string | null; completed_with_buffer?: string | null};

export type TCarouselUser = {
  first_name: string;
  influencer_id: number;
  last_name: string;
  picture: string;
  username: string;
};

export const assetName = (asset: Partial<PromotionCampaignAssetSerializer>) => {
  const transformedTitle = snakeCase(asset.title ?? "");
  const url = asset.media_file!;
  const extension = url.split(".").at(-1)?.split("?")[0];

  return `${transformedTitle}.${extension}`;
};

/**
 * Mirrors the backend `Campaign.completed_buffer_days` rule: the grace days added after the
 * commercial phase deadline. Keep in sync with `CAMPAIGN_COMPLETED_BUFFER_DAYS*` in the backend
 * settings — the campaign form has to add the same buffer the backend will apply on save.
 */
export const getCampaignBufferDays = ({
  contentApproval,
  isEvent,
  isExtended,
  isUgc,
}: {
  contentApproval: boolean;
  isEvent: boolean | undefined;
  isExtended: boolean;
  isUgc: boolean;
}) => {
  if (isEvent || isUgc || isExtended) {
    return 0;
  }

  return contentApproval ? CAMPAIGN_BUFFER_DAYS_CONTENT_APPROVAL : CAMPAIGN_BUFFER_DAYS;
};

export const getCampaignDeadline = (campaign: Pick<CampaignFeedSerializer, "deadline" | "event" | "on_going">) => {
  const deadline = dayjs(campaign.deadline);

  if (campaign.event && campaign.on_going && dayjs(deadline).diff(dayjs(), "days") < 0) {
    return dayjs(deadline).add(3, "days");
  }

  return deadline;
};

export const getEndDateWithBuffer = ({source}: {source: TCompletedWithBuffer}) =>
  dayjs(source.completed_with_buffer ?? source.completed);

export const isCampaignInBufferPhase = ({campaign}: {campaign: TCompletedWithBuffer}) => {
  if (!campaign.completed || !campaign.completed_with_buffer) {
    return false;
  }

  const bufferEnd = dayjs(campaign.completed_with_buffer);

  return !bufferEnd.isSame(campaign.completed) && dayjs().isBefore(bufferEnd);
};

export const getSourceContentAsCarousel = ({
  sourceContent,
  user,
}: {
  sourceContent: TInstagramContent[];
  user: TCarouselUser;
}): TInstagramContent | undefined =>
  sourceContent.length
    ? {
        applicationId: sourceContent[0].applicationId,
        id: sourceContent[0].id,
        image: sourceContent[0].image,
        instagram_media: {
          children: sourceContent.map(item => ({...item.instagram_media!, media_id: ""})),
          created: sourceContent[0].instagram_media?.created ?? sourceContent[0].instagram_media?.timestamp,
          featured: false,
          id: sourceContent[0].instagram_media!.id,
          media_file: sourceContent[0].instagram_media!.media_file,
          media_product_type: "SOURCE",
          media_type: "CAROUSEL_ALBUM",
          media_url: sourceContent[0].instagram_media!.media_url,
          purchased: sourceContent.every(item => !!item.instagram_media?.purchased)
            ? sourceContent[0].instagram_media?.purchased
            : null,
          thumbnail_url: sourceContent[0].instagram_media!.thumbnail_url,
        },
        user,
      }
    : undefined;

export const getSourceContentFromCampaignApplication = ({
  campaignApplication,
  campaignType,
}: {
  campaignApplication: TCampaignApplicationAccepted | TCampaignApplicationReporting;
  campaignType: TCampaignType;
}): TInstagramContent[] =>
  (campaignApplication?.source_materials ?? campaignApplication.instagram_media?.source_materials ?? []).map(item => ({
    applicationId: campaignApplication.id,
    id: item.id,
    image: (item.thumbnail_file ?? item.media_file)!,
    instagram_media: {
      ...item,
      campaign_application: undefined,
      media_product_type: "SOURCE" as const,
      media_type: (item.thumbnail_file ? "VIDEO" : "IMAGE") as "VIDEO" | "IMAGE",
    },
    user: getUserFromCampaignApplication({campaignApplication, campaignType}),
  }));

export const getCarouselItemFromSubmission = ({
  influencer,
  isInstagram,
  submission,
  user,
}: {
  influencer?: CampaignManagementInfluencerSerializer;
  isInstagram: boolean;
  submission: TContentSubmission;
  user?: TCarouselUser;
}): TCarouselMedia => {
  const contentId = submission.status === "PUBLISHED" ? submission.id : 1_000_000_000 + submission.id;

  return {
    applicationId: submission.application_id,
    caption: submission.caption!,
    date: submission.review_by_date,
    id: contentId,
    image: submission.thumbnail_file!,
    instagram_media: {
      children: submission?.carousel_medias,
      comments_count: submission.comment_count,
      id: contentId,
      impression_count: submission.impression_count,
      like_count: submission.like_count,
      liked: submission.liked,
      permalink: submission.permalink,
      media_file: submission.media_file!,
      media_product_type: (submission.content_type === "REEL"
        ? "REELS"
        : submission.content_type === "TIKTOK_VIDEO"
          ? "TIKTOK"
          : submission.content_type === "STORY"
            ? "STORY"
            : "POST") as TContent,
      media_type: submission.media_type || "VIDEO",
      media_url: submission.media_file!,
      play_count: submission.play_count,
      reach_count: submission.reach_count,
      save_count: submission.save_count,
      share_count: submission.share_count,
      thumbnail_file: submission.thumbnail_file!,
      thumbnail_url: submission.thumbnail_file!,
      timestamp: submission.publish_date,
      video_code: submission.video_code,
    },
    isRevisionAvailable:
      submission.revision_count !== undefined && submission.revision_count < CONTENT_APPROVAL_MAX_REVISIONS,
    submissionId: submission.status !== "PUBLISHED" ? submission.id : undefined,
    submissionStatus: submission.status,
    user: user ?? {
      first_name: influencer?.user.first_name!,
      influencer_id: influencer?.uuid!,
      picture: influencer?.picture!,
      username: isInstagram ? influencer?.user.username : influencer?.user.tiktok_username!,
    },
  };
};

export const getContentTypeTranslationKeys = (contentType: TContent) => {
  switch (contentType) {
    case "POST":
      return {
        titleTranslationKey: "postsCount",
        noContentPlaceholderTranslationKey: "noPostsContent",
      } as const;
    case "STORY":
      return {
        titleTranslationKey: "storiesCount",
        noContentPlaceholderTranslationKey: "noStoriesContent",
      } as const;
    case "REEL":
      return {
        titleTranslationKey: "reelsCount",
        noContentPlaceholderTranslationKey: "noReelsContent",
      } as const;
    case "UGC":
      return {
        titleTranslationKey: "ugcCount",
        noContentPlaceholderTranslationKey: "noUgcContent",
      } as const;
    case "SOURCE":
      return {
        titleTranslationKey: "sourceContent",
        noContentPlaceholderTranslationKey: "noSourceContent",
      } as const;
    case "TIKTOK":
      return {
        titleTranslationKey: "tiktokCount",
        noContentPlaceholderTranslationKey: "noTiktokContent",
      } as const;
    default:
      throw new Error("Unknown content type");
  }
};

export const getStoryContentsFromCampaignApplication = ({
  campaignApplication,
  campaignType,
}: {
  campaignApplication?: TCampaignApplicationAccepted | TCampaignApplicationReporting;
  campaignType: TCampaignType;
}): TInstagramContent[] => {
  const user = getUserFromCampaignApplication({campaignApplication, campaignType});

  return campaignApplication?.instagram_media?.has_media
    ? campaignApplication.instagram_media.stories.map(item => ({
        applicationId: campaignApplication.id,
        buyoutsPerContent: campaignApplication?.tokens_per_content ?? undefined,
        id: item.id,
        image: (item.thumbnail_file ?? item.thumbnail_url)!,
        instagram_media: item as TInstagramMedia,
        user,
      }))
    : (campaignApplication?.stories?.map(item => ({
        applicationId: campaignApplication.id,
        buyoutsPerContent: campaignApplication?.tokens_per_content ?? undefined,
        id: item.id,
        image: item.image!,
        instagram_media: {
          featured: item.featured,
          id: item.id,
          media_file: item.image!,
          media_product_type: "STORY",
          media_type: "IMAGE",
          thumbnail_file: item.image!,
        },
        user,
      })) ?? []);
};

export const getContentIcons = () => ({
  POST: PostIcon,
  REEL: ReelIcon,
  STORIES: StoryIcon,
  TIKTOK_VIDEO: TiktokIcon,
  UGC: UgcIcon,
});

export const getSubmissionContentCountsByStatus = ({
  status,
  submissions,
}: {
  status?: TSubmissionStatus | TSubmissionStatus[];
  submissions?: TContentSubmission[];
}) => {
  const statusArray = Array.isArray(status) ? status : [status];

  return countBy(
    submissions?.filter(submission => statusArray.includes(submission.status)),
    submission => (submission.content_type === "STORY" ? "STORIES" : submission.content_type),
  );
};

export const getInstagramBonusPoolLimit = (sellingPrice: number | null) =>
  Math.max(Math.ceil((100 - (sellingPrice ?? 0)) / 1.5), BONUS_POOL_MIN_CASH_REWARD);

export const getCurrentStatsPerRequested = ({
  currentStats,
  requestedStats,
}: {
  currentStats: Dictionary<number>;
  requestedStats: TContentStats;
}) =>
  Object.fromEntries(
    Object.entries(requestedStats).map(([key, value]) => [
      key,
      {
        done: currentStats[key as keyof TContentStats] ?? 0,
        requested: value.requested,
      },
    ]),
  );

export const getUserFromCampaignApplication = ({
  campaignApplication,
  campaignType,
}: {
  campaignApplication?: TCampaignApplicationAccepted | TCampaignApplicationReporting;
  campaignType: TCampaignType;
}): TCarouselUser => ({
  first_name: campaignApplication?.influencer!.user.first_name!,
  influencer_id: campaignApplication?.influencer!.uuid!,
  last_name: campaignApplication?.influencer!.user.last_name!,
  picture: campaignApplication?.influencer!.picture!,
  username:
    campaignType === "tiktok"
      ? campaignApplication?.influencer!.user.tiktok_username!
      : campaignApplication?.influencer!.user.username!,
});
